/**
 * RWBR — the EZRA BOTTOM GROUP CHURN (BGC).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE SOURCE, AND IT IS ONE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `EZRAS2RulesFinal.docx`, §3.12, printed under the heading ***EZRA Bottom
 * Group Chrun*** — the misspelling is the document's own and is the reason a
 * search for "Churn" finds the drafting cross-reference (§3.10) and nothing
 * else. The section runs pp. 18-19 per the rules index. It is transcribed here
 * in full, in the order it is printed, because every ruling below is an
 * argument about one of these sentences:
 *
 *   *"A Bottom Group Churn (BGC) can occur on any Regular or Final race turn.
 *    It is not a turn type but rather a Prelude Action that will precede any
 *    turn if allowed by race circumstances and a random dice roll."*
 *
 *   *"An BGC is activated by a D10 roll of 1 on the EZRA PRELUDE TRIGGER TABLE
 *    (PTT). (See Rules Appendix.)"*
 *
 *   *"BGC is activated before the running of the regular turn. The regular turn
 *    will follow the completion of the BGC and the filling of any gaps that
 *    might result (RB/vi)."*
 *
 *   *"Multiple Events (RB/iii) are ignored during a Churn."*
 *
 *   *"Yellow Flags that arise from the Churn action are deferred until the
 *    Regular or Final Turn is completed."*
 *
 *   *"All cars in Bottom Group (BG), including the Lap Down Sub-Group (LDSG)
 *    are eligible."*
 *
 *   ***Implementing The Churn:***
 *   *"First Step - Roll Race Cam Dice. If the black die is a 6 play the BGC as
 *    duel. The Bottom Group Driver Finder Table will be used to select the
 *    dueling cars. The duel is resolved using normal procedures. The duel ends
 *    the BGC."*
 *   *"Ignore the black die for all other BGC purposes."*
 *   *"If no duel occurs, the MM dice are used on the Track Card to select a BGC
 *    Challenge Quality."*
 *   *"Cars assigned the designated quality will participate in the BGC."*
 *   *"Starting at the back of the BG, each car with the designated quality will
 *    challenge ahead using the BG Driver Finder Table."*
 *   *"If the finder designates a car behind the challenger, the challenge is
 *    ignored."*
 *   *"If the challenger successfully challenges a car ahead the two exchange
 *    positions. No penalties accrue for failure."*
 *   *"If the successful challenger is a car in the LDSG, EZRA challenge and
 *    movement rules for that group are observed."*
 *   *"During a BGC cars will not advance to Middle Group."*
 *   *"Problem and Trouble resolution: All problem or trouble incidents during
 *    the BGC are resolved as single car events in the same fashion as for duels
 *    (RB/ix). The Advanced Trouble Matrix rule is not applied."*
 *   *"Upon completion of all challenges and the filling of any gaps, the BGC
 *    ends."*
 *
 *   ***BGC Performance Chips***
 *   *"Performance Chips will be allowed, but these have no affect outside the
 *    BG. They function only within the BG and then only during a BGC."*
 *   *"BGC Performance Chips are designated by a token of a different color from
 *    that used for PC's in the Middle and Top Groups that arise from standard
 *    RWBR play procedures."*
 *   *"The car that occupies the first (lead) position in the BG (BG#20) is
 *    designated the car on the 'Porch'. If it is found to possess the active
 *    BGC quality, it may challenge a MG car in the MG 19th position. If
 *    successful, it does not advance to the MG, but receives a BGC performance
 *    chip."*
 *   *"Qualities are played during the Churn in the normal fashion, but only
 *    within the BG. If a *quality moves a car to the "porch" on the first
 *    challenge, he can then challenge the MG car in the 19th position. If
 *    successful is awarded a BG PC and holds the 20th position."*
 *   *"BG PC's cannot be used in connection with a feud/fume involving a higher
 *    group car during a Regular Turn."*
 *   *"Cars in BG with PCs that move to Middle Group during a Regular Turn lose
 *    their BGC PC(s) when exiting the BG."*
 *
 *   ***Other BGC Notes***
 *   *"A BGC can occur only once each one third race turn increment. Ignore any
 *    additional BGCs that are rolled in an increment where a BGC has already
 *    occurred. However, there can always be a BGC on the Final Turn of a race,
 *    even with a BGC earlier in the final one-third race turn increment.
 *    Aborted or ignored Churns are not counted against occurrence
 *    limitations."*
 *   *"BGCs do not occur on a Pit Turn."*
 *   *"Yellow flags during the BGC do not take effect until the end of the
 *    Regular Turn that follows the BGC."*
 *   *"When a BGC is activated, all other aspects of that turn as they affect
 *    the Top, Middle and Bottom Groups, including a Bottom Group Surge, are
 *    resolved after the BGC has completed."*
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-29 — THE TRIGGER IS PRINTED, SO THE PTT DOES NOT BLOCK THIS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CROSS_REFERENCE.md` B8 records the EZRA PRELUDE TRIGGER TABLE as missing
 * from the corpus and lists three subsystems as blocked on it. It blocks ONE.
 *
 * The BGC names its own face in prose — *"activated by a D10 roll of 1"* — in
 * exactly the shape the Race Start Event does (D-EZ-22), so the rate is
 * printed: **one eligible turn in ten.** The PTT is a lookup that routes a d10
 * face to whichever prelude is eligible; where the prose already gives the
 * number, the table has nothing left to say.
 *
 * **The Bottom Group SURGE is the real casualty and is NOT built.** See the
 * closing note at the foot of this file. Its text is *"An BGS is activated by a
 * D10 roll on the EZRA PRELUDE TRIGGER TABLE (PTT)"* — a roll with **no number
 * at all**. Inventing one would be inventing the rate of a whole subsystem.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-30 — "REGULAR OR FINAL" EXCLUDES A YELLOW FLAG TURN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The sheet excludes pit turns by name (*"BGCs do not occur on a Pit Turn"*)
 * and says nothing whatever about yellow flag turns. Under D-EZ-1 the base
 * rulebook governs where EZRA is silent, and the base rulebook settles it
 * without anyone having to reason about what "Regular" means:
 *
 *   RB/xi — *"In a YELLOW FLAG turn, no RACE CAM action occurs."*
 *
 * The Churn's own FIRST STEP is *"Roll Race Cam Dice"*, and both branches of the
 * procedure read them — the black die decides duel-or-challenge, the metallic
 * dice pick the quality off the track card. A turn in which the race cam is not
 * read is a turn in which step one of the Churn cannot be performed. So the
 * exclusion is not a ruling about the word "Regular"; it falls out of the
 * printed procedure being impossible.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-31 — WHAT "ABORTED" MEANS, AND WHY IT IS A RATE QUESTION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"Aborted or ignored Churns are not counted against occurrence limitations."*
 *
 * "Ignored" is defined by the sentence in front of it: a Churn rolled in an
 * increment that has already had one. "Aborted" is the other word and is left
 * undefined, and it decides how often a Churn can happen — so it is a rate
 * ruling, not a tidiness one.
 *
 * **Ruled: a Churn OCCURRED (and spends its increment) only if it actually did
 * something** — it was played as a duel, or at least one car made at least one
 * challenge. Anything short of that is ABORTED and costs nothing. Three cases
 * abort:
 *
 *   1. Fewer than two cars in the BOTTOM group. There is nothing to churn:
 *      no pair to duel and nobody to exchange positions with.
 *   2. The metallic dice read a cell that is not a quality (see D-EZ-32).
 *   3. A quality was designated and no eligible BOTTOM group car holds it.
 *
 * The reasoning is that "aborted" in plain English is *started and not
 * completed*, which is precisely the shape of all three; and reading it any
 * other way would let a Churn that moved nobody eat a whole third of the race.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-32 — A NON-QUALITY CELL DESIGNATES NO QUALITY. IT IS NOT A PROBLEM.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"the MM dice are used on the Track Card to select a BGC Challenge Quality."*
 *
 * Roughly one metallic total in five reads PROBLEM, TROUBLE or RARE rather than
 * a quality. The sheet does not say what happens then.
 *
 * **Ruled: no quality is selected, and the Churn aborts.** It is tempting to
 * read the later sentence — *"All problem or trouble incidents during the BGC
 * are resolved as single car events"* — as authorising a PROBLEM cell to be
 * played as a bottom-group problem. It is printed AFTER the whole challenge
 * procedure and says "during the BGC", which is the challenge sequence: triples
 * (RB/vi) and the triangle symbol (RB/xvi) are exactly the incidents that arise
 * *during* it, and they are what that sentence tells you how to scope.
 *
 * Playing a PROBLEM cell as a bottom-group problem would ADD an incident the
 * sheet never asks for, on about one Churn in six. Reading the sentence the
 * narrow way subtracts nothing that is printed. Where the two readings differ,
 * the one that invents no result wins. **The alternative is recorded here so
 * that the day the PTT or a fuller §3.12 turns up, this is a one-line change
 * and a re-measurement rather than an excavation.** The harness counts these
 * separately (`no quality designated`) so the size of the question is always on
 * screen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-33 — THE CHURN CHALLENGE IS A RED-WHITE-BLUE CHALLENGE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"each car with the designated quality will challenge ahead using the BG
 * Driver Finder Table."*
 *
 * EZRA replaces one step of the base procedure — who you are challenging — and
 * says nothing about the rest of it. Under D-EZ-1 the base rulebook governs the
 * rest, unchanged (RB/vi-vii): the full red-white-blue roll, red against the
 * challenger's gas pedal, blue against the defender's brake pedal with p.vii's
 * bonuses, triples voiding the challenge into a PROBLEM, and the triangle
 * symbol (RB/xvi) doing the same on a red 6.
 *
 * The WHITE die is still rolled and still logged. Its *selection* job is the
 * one EZRA supersedes — the Driver Finder picks the target instead of a board
 * box — but its other job, being the middle third of a triple, is untouched.
 * Dropping it would silently delete the triples rule from every Churn
 * challenge, and would change the odds of a triple from 1/36 to zero.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-34 — CLOSED. THE BGC PERFORMANCE CHIP IS NOW SPENDABLE (see D-EZ-39)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D-EZ-34 used to record that the porch award was counted and nothing else: the
 * chip was in `bgcChipsAwarded` and on the ledger, and no spendable token
 * existed, so *"Performance Chips will be allowed"* had nothing to allow and
 * every Churn challenge was decided on the dice alone.
 *
 * It is built now. The representation and its containment argument are
 * **D-EZ-39, at the head of `chips.ts`** — that is where it belongs, because the
 * whole point of the shape is that no module outside `chips.ts` has to know the
 * rule. In one paragraph: a BG chip lives in `Car.bgChips`, never in
 * `Car.chips`, so no existing chip contest in the engine can see it;
 * `awardBgChip` refuses to create one outside the BOTTOM group; and
 * `restoreChips` destroys one the moment its owner leaves the BOTTOM group,
 * which is the printed sentence *"Cars in BG with PCs that move to Middle Group
 * during a Regular Turn lose their BGC PC(s) when exiting the BG"*.
 *
 * What this module adds is the one place the chip is worth something:
 *
 *   *"Performance Chips will be allowed, but these have no affect outside the
 *    BG. They function only within the BG and then only during a BGC."*
 *
 * A Churn challenge therefore runs the ordinary chip contest — `challenge.ts`'s
 * `resolveChipContest`, the same function the quality challenges and the duels
 * use, so EZRA §3.1's dominance rule and RB/xiv's odd/even die are not
 * reimplemented here — with `bgChipsSpendable` supplying both sides' counts.
 * Reusing it is the point: a second copy of the chip rules is how two versions
 * of a rule start to disagree, and this one has already been corrected once
 * (see the note in `resolveChipContest` about the binary reading).
 *
 * TWO CONSEQUENCES, BOTH MEASURED BY `npm run surge`, NEITHER HIDDEN:
 *
 * **(a) The porch chip recycles.** RB/xiv's solitaire rule is that a driver who
 * holds a chip automatically spends it to salvage a failed challenge. The porch
 * challenge's prize is a chip. So a car that sits on the Porch holding a BG chip
 * wins the porch challenge every single time and ends the Churn with the count
 * it started with. That is odd, and it is what the two printed rules say when
 * composed, so it is what is built — the alternative reading (a porch challenge
 * gains no track position, so there is nothing for RB/xiv to "salvage" and no
 * chip is spent) is a carve-out based on inferred purpose, and this project's
 * standing test is that where two readings differ the one that invents nothing
 * wins. `PORCH_CHALLENGE_SPENDS_CHIPS` names the reading so that flipping it is
 * one line, and the harness prints the porch win rate split by whether the car
 * held a chip, so the cost of the ruling is a number.
 *
 * **(b) The churn DUEL does not offer BG chips.** *"If the black die is a 6 play
 * the BGC as duel"*, and `duel.ts` owns duels — it reads `Car.chips`, which a
 * BOTTOM group car cannot hold. Teaching it about a third pile would mean
 * either a second copy of the chip contest or lending BG chips into `Car.chips`
 * across a module boundary, and both are exactly the "every caller remembers a
 * rule" failure the representation exists to prevent. So a Churn played as a
 * duel is resolved on the dice, the harness COUNTS the duels in which a BG chip
 * was held and could not be offered, and that count is the size of the gap. It
 * is a gap, not a fabrication.
 */

import type { Car, Quality, RaceCamResult } from './types.ts';
import { QUALITIES, groupOf, hasQuality, isStarred } from './types.ts';
import type { Dice } from './dice.ts';
import type { DiceRecord, Modifier, PendingProblem, RuleEvent } from './ruleEvent.ts';
import { diffOrder, nameOf, swapPositions } from './ruleEvent.ts';
import {
  brakePedal,
  CHALLENGE_ROLL,
  oddEvenRoll,
  resolveChipContest,
  type BrakeCalc,
  type ChipOutcome,
} from './challenge.ts';
import {
  awardBgChip,
  bgChipsOf,
  bgChipsSpendable,
  spendBgChips,
  CITE_BG_CHIP,
  CITE_BG_CHIP_CAP,
  type BgChipRefusal,
} from './chips.ts';
import { rollDriverFinder } from './problem.ts';
import { troubleFieldFromGrid, type TroubleField } from './trouble.ts';
import {
  ESCAPE_CHALLENGES,
  canChallengeOutOfLdsg,
  returnToLeadLap,
  type LapsDownTracker,
} from './lapsDown.ts';
import type { RaceThird } from './repair.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_BGC =
  'EZRA-S2 §3.12 Bottom Group Churn — "A Bottom Group Churn (BGC) can occur on any Regular or ' +
  'Final race turn. It is not a turn type but rather a Prelude Action that will precede any turn ' +
  'if allowed by race circumstances and a random dice roll."';

export const CITE_BGC_TRIGGER =
  'D-EZ-29 — EZRA-S2 §3.12: "An BGC is activated by a D10 roll of 1 on the EZRA PRELUDE TRIGGER ' +
  'TABLE (PTT)." The face is printed in prose, so the missing PTT (CROSS_REFERENCE B8) does not ' +
  'block the Churn; it blocks only the Bottom Group Surge, whose text prints no number at all.';

export const CITE_BGC_WHEN =
  'EZRA-S2 §3.12 — "BGCs do not occur on a Pit Turn." + D-EZ-30: a YELLOW FLAG turn is excluded ' +
  'because the Churn\'s first step is "Roll Race Cam Dice" and RB/xi says "In a YELLOW FLAG turn, ' +
  'no RACE CAM action occurs" — step one cannot be performed.';

export const CITE_BGC_INCREMENT =
  'EZRA-S2 §3.12 Other BGC Notes — "A BGC can occur only once each one third race turn increment. ' +
  'Ignore any additional BGCs that are rolled in an increment where a BGC has already occurred. ' +
  'However, there can always be a BGC on the Final Turn of a race, even with a BGC earlier in the ' +
  'final one-third race turn increment. Aborted or ignored Churns are not counted against ' +
  'occurrence limitations."';

export const CITE_BGC_ORDER =
  'EZRA-S2 §3.12 — "When a BGC is activated, all other aspects of that turn as they affect the ' +
  'Top, Middle and Bottom Groups, including a Bottom Group Surge, are resolved after the BGC has ' +
  'completed." The Churn therefore runs ahead of every other prelude, the Race Start Event ' +
  'included.';

export const CITE_BGC_DUEL =
  'EZRA-S2 §3.12 Implementing The Churn — "First Step - Roll Race Cam Dice. If the black die is a ' +
  '6 play the BGC as duel. The Bottom Group Driver Finder Table will be used to select the ' +
  'dueling cars. The duel is resolved using normal procedures. The duel ends the BGC."';

export const CITE_BGC_QUALITY =
  'EZRA-S2 §3.12 — "Ignore the black die for all other BGC purposes. If no duel occurs, the MM ' +
  'dice are used on the Track Card to select a BGC Challenge Quality. Cars assigned the ' +
  'designated quality will participate in the BGC."';

export const CITE_BGC_CHALLENGE =
  'EZRA-S2 §3.12 — "Starting at the back of the BG, each car with the designated quality will ' +
  'challenge ahead using the BG Driver Finder Table. If the finder designates a car behind the ' +
  'challenger, the challenge is ignored. If the challenger successfully challenges a car ahead ' +
  'the two exchange positions. No penalties accrue for failure." + D-EZ-33: the roll itself is ' +
  'the base game\'s red-white-blue challenge (RB/vi-vii), because EZRA replaces only the step ' +
  'that names the defender.';

export const CITE_BGC_NO_PROMOTION =
  'EZRA-S2 §3.12 — "During a BGC cars will not advance to Middle Group."';

export const CITE_BGC_LDSG =
  'EZRA-S2 §3.12 — "If the successful challenger is a car in the LDSG, EZRA challenge and ' +
  'movement rules for that group are observed." (§3.11: "the driver moves to the last position in ' +
  'the LLSG, subject to Pit crew rating (A-B-C automatic, D has to successfully Challenge twice, ' +
  'E can never challenge out of the LDSG)".)';

export const CITE_BGC_INCIDENTS =
  'EZRA-S2 §3.12 — "Problem and Trouble resolution: All problem or trouble incidents during the ' +
  'BGC are resolved as single car events in the same fashion as for duels (RB/ix). The Advanced ' +
  'Trouble Matrix rule is not applied."';

export const CITE_BGC_MULTIPLE_EVENTS =
  'EZRA-S2 §3.12 — "Multiple Events (RB/iii) are ignored during a Churn."';

export const CITE_BGC_DEFERRED_YELLOW =
  'EZRA-S2 §3.12 — "Yellow Flags that arise from the Churn action are deferred until the Regular ' +
  'or Final Turn is completed." / "Yellow flags during the BGC do not take effect until the end ' +
  'of the Regular Turn that follows the BGC."';

export const CITE_BGC_PORCH =
  'EZRA-S2 §3.12 BGC Performance Chips — "The car that occupies the first (lead) position in the ' +
  'BG (BG#20) is designated the car on the “Porch”. If it is found to possess the active ' +
  'BGC quality, it may challenge a MG car in the MG 19th position. If successful, it does not ' +
  'advance to the MG, but receives a BGC performance chip."';

export const CITE_BGC_PORCH_STAR =
  'EZRA-S2 §3.12 — "Qualities are played during the Churn in the normal fashion, but only within ' +
  'the BG. If a *quality moves a car to the “porch” on the first challenge, he can then ' +
  'challenge the MG car in the 19th position. If successful is awarded a BG PC and holds the ' +
  '20th position."';

export const CITE_BGC_ELIGIBLE =
  'EZRA-S2 §3.12 — "All cars in Bottom Group (BG), including the Lap Down Sub-Group (LDSG) are ' +
  'eligible."';

/**
 * The chip sentence, quoted once in `chips.ts` and re-used here rather than
 * re-typed: two copies of a verbatim quotation are two chances to mistype it.
 */
export const CITE_BGC_CHIPS_ALLOWED =
  `${CITE_BG_CHIP} (D-EZ-39; the contest itself is RB/xiv + EZRA-S2 §3.1, run by challenge.ts's ` +
  'resolveChipContest so that there is exactly one implementation of it.)';

export const CITE_BGC_PORCH_DEFENCE =
  'D-EZ-40 — the MIDDLE group car in 19th does not defend the Porch challenge with a performance ' +
  'chip. EZRA-S2 §3.12: "If successful, it does not advance to the MG, but receives a BGC ' +
  'performance chip." He loses no track position, so there is nothing for RB/xiv\'s "salvage a ' +
  'failed challenge or defense" to salvage and nothing on EZRA-S2 §3.1\'s list ("To defend a ' +
  'Challenge... To defend the loss of position to a relegated driver") that he could be defending.';

export const CITE_BGC_DUEL_NO_CHIPS =
  'D-EZ-39(b) — a Churn played as a DUEL is resolved on the dice. duel.ts owns duels and reads ' +
  'Car.chips, which no BOTTOM group car may hold (RB/xv); a BG chip is a separate pile by design ' +
  '(EZRA-S2 §3.12, "a token of a different color"). Recorded and counted rather than faked.';

/**
 * Does a challenger spend a BG chip to salvage a FAILED Porch challenge?
 *
 * `true` = the literal composition of RB/xiv and §3.12: the chip is spent, the
 * challenge succeeds, and a chip is awarded — net zero, so a chipped Porch car
 * never loses the porch challenge. See consequence (a) in the header. Named so
 * the alternative reading is one edit and a re-measurement.
 */
export const PORCH_CHALLENGE_SPENDS_CHIPS = true;

/**
 * The Bottom Group SURGE lives in `bottomGroupSurge.ts`.
 *
 * Its PROCEDURE is fully printed and fully built. Its TRIGGER FACE is not
 * printed anywhere in the corpus, so it is an explicit open ruling (D-EZ-35) and
 * the rule ships **off** — an off-by-default rule that can be switched on is
 * honest; an on-by-default invented rate silently changes every race. Kept here
 * as a citation so the Churn's own file still says where its sibling went.
 */
export const CITE_BGS_SEE_SURGE_MODULE =
  'EZRA-S2 §3.13 Bottom Group Surge — built in bottomGroupSurge.ts. The procedure is printed in ' +
  'full; the trigger FACE is not printed anywhere ("An BGS is activated by a D10 roll on the EZRA ' +
  'PRELUDE TRIGGER TABLE (PTT). (See Rules Appendix.)" — no face, and the PTT is not in the corpus, ' +
  'CROSS_REFERENCE B8), so the trigger is OPEN RULING D-EZ-35 and the rule is OFF by default.';

// ---------------------------------------------------------------------------
// The trigger
// ---------------------------------------------------------------------------

/** "An BGC is activated by a D10 roll of 1." One eligible turn in ten. */
export const BGC_TRIGGER_FACE = 1;
export const BGC_TRIGGER_SIDES = 10;

/** "If the black die is a 6 play the BGC as duel." All 31 track cards print 6 = DUEL. */
export const BGC_DUEL_BLACK_FACE = 6;

/** "the first (lead) position in the BG (BG#20)" — the car on the Porch. */
export const PORCH_POSITION = 20;

/** "a MG car in the MG 19th position" — the last seat on Board B. */
export const PORCH_TARGET_POSITION = 19;

/** Ledger label for the Churn's own prelude die. */
export const BGC_TRIGGER_LABEL = 'BGC trigger (prelude)';

/**
 * Is a Churn even eligible on this turn?
 *
 * *"can occur on any Regular or Final race turn"*, *"BGCs do not occur on a Pit
 * Turn"*, and D-EZ-30 for the yellow flag turn. Pure, and separate from the
 * roll, so the eligibility rule is testable without spending a die.
 */
export function isChurnTurn(input: { isPitTurn: boolean; isYellowTurn: boolean }): boolean {
  if (input.isPitTurn) return false;
  if (input.isYellowTurn) return false;
  return true;
}

/**
 * Roll the trigger.
 *
 * Separate from resolution for the same reason `rollRseTrigger` is: a caller
 * can ask "does a Churn happen?" without paying for the rest, and the rate is
 * measurable on its own — which is the only way the 10% in the header can be
 * checked against reality rather than asserted.
 */
export function rollChurnTrigger(dice: Dice): { fired: boolean; roll: number; record: DiceRecord } {
  const roll = dice.d10();
  const fired = roll === BGC_TRIGGER_FACE;
  return {
    fired,
    roll,
    record: {
      kind: 'd10',
      dice: [roll],
      total: roll,
      label: BGC_TRIGGER_LABEL,
      flags: { fired },
    },
  };
}

// ---------------------------------------------------------------------------
// The once-per-third limit
// ---------------------------------------------------------------------------

/**
 * *"A BGC can occur only once each one third race turn increment."*
 *
 * LIKE THE CHART B INDEX CARD AND THE LDSG TRACKER, THIS BELONGS TO THE RACE,
 * NOT THE TURN. The whole content of the rule is memory of an earlier turn, so
 * a tracker rebuilt each turn would enforce nothing at all while looking
 * exactly like it worked — the failure mode this project has already shipped
 * once and caught by counting. `race.ts` makes one and hands the same object
 * down every turn.
 *
 * The thirds are `repair.ts`'s `raceThird`, which is the same "FIRST 1/3 /
 * MIDDLE 1/3 / FINAL 1/3" the boards and the EZRA track cards print
 * ("TURN COUNT: FIRST 1/3 [1-16] MIDDLE 1/3 [17-32] FINAL 1/3 [33-50]"). One
 * definition of a third, used by both rules that need one.
 *
 * Every counter here is surfaced rather than swallowed. `triggered` minus
 * `ignored` minus `aborted` must equal `occurred`, and the harness prints all
 * four — a limit nobody counts is indistinguishable from a limit that is not
 * being applied.
 */
export class ChurnTracker {
  private readonly spent = new Set<RaceThird>();

  /** d10s that came up 1 on an eligible turn. */
  triggered = 0;
  /** "Ignore any additional BGCs that are rolled in an increment where a BGC has already occurred." */
  ignored = 0;
  /** Churns that started and did nothing (D-EZ-31). They cost the increment nothing. */
  aborted = 0;
  /** Churns that actually happened, and therefore spent their increment. */
  occurred = 0;

  /**
   * May a Churn be played on this turn?
   *
   * *"However, there can always be a BGC on the Final Turn of a race, even with
   * a BGC earlier in the final one-third race turn increment."* — the final
   * turn is exempt outright, which is why `isFinalTurn` short-circuits ahead of
   * the set.
   */
  allows(third: RaceThird, isFinalTurn: boolean): boolean {
    if (isFinalTurn) return true;
    return !this.spent.has(third);
  }

  /**
   * Record what a triggered Churn turned out to be.
   *
   * The final turn's exemption is one-directional: it lets a Churn happen
   * despite a spent increment, and a Churn played there does not un-spend or
   * re-spend anything.
   */
  note(third: RaceThird, isFinalTurn: boolean, outcome: 'occurred' | 'aborted'): void {
    if (outcome === 'aborted') {
      this.aborted += 1;
      return;
    }
    this.occurred += 1;
    if (!isFinalTurn) this.spent.add(third);
  }

  /** A trigger that came up in an increment already used. */
  noteIgnored(): void {
    this.ignored += 1;
  }

  /** Increments that have had their Churn. For the harness and for tests. */
  get spentIncrements(): RaceThird[] {
    return [...this.spent];
  }
}

// ---------------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------------

export type ChurnMoment =
  /** The black die was a 6: the Churn is a duel and ends with it. */
  | 'churn.duel'
  /** Challenges were made. */
  | 'churn.challenges'
  /** A car took the Porch challenge into 19th. */
  | 'churn.porch'
  /** Started and did nothing (D-EZ-31). */
  | 'churn.aborted';

/** One car's attempt inside the Churn. */
export interface ChurnChallenge {
  challengerId: string;
  challengerName: string;
  /** 1 = his challenge; 2 = the extra one a starred quality grants (RB/vii). */
  attempt: 1 | 2;
  /** Where he was when he made it. */
  from: number;
  /** The car the BG Driver Finder named, or the MG 19th car on a Porch challenge. */
  defenderId: string | null;
  defenderName: string | null;
  defenderPosition: number | null;
  /** True when this was the Porch challenge into the MIDDLE group's last seat. */
  porch: boolean;
  /** "If the finder designates a car behind the challenger, the challenge is ignored." */
  ignored: boolean;
  ignoredReason: string | null;
  /** RB/vi triples, or RB/xvi's triangle on a red 6. The challenge did not happen. */
  voided: boolean;
  gasOk: boolean;
  defended: boolean;
  brake: BrakeCalc | null;
  /** The outcome BEFORE any BG performance chip changed it. */
  rawSuccess: boolean;
  success: boolean;
  /** BG chips each side could spend at the moment of the roll (D-EZ-39). */
  bgChipsHeld: { challenger: number; defender: number };
  /** What the chip contest did. 'none' = chips were never in play. */
  chip: ChipOutcome;
  /** RB/xiv's extra die, rolled only when both sides held a BG chip. */
  oddEvenDie: number | null;
  /** The Porch's reward: "receives a BGC performance chip" (D-EZ-39). */
  awardedBgChip: boolean;
  /** Set when the award was refused — outside the BG, or at §3.1's cap of two. */
  bgChipRefused: BgChipRefusal | null;
  /** Set when a laps-down winner banked a win or got his lap back (§3.11). */
  ldsg: { banked: number; needed: number; escaped: boolean } | null;
}

export interface BottomGroupChurnResult extends RuleEvent {
  eventType: 'bottom-group-churn';
  momentTag: ChurnMoment;
  /** The race cam roll the Churn's first step made. */
  black: number;
  metallic: [number, number];
  metallicTotal: number;
  /** "If the black die is a 6 play the BGC as duel." */
  asDuel: boolean;
  /**
   * The two cars the BOTTOM GROUP DRIVER FINDER named for the duel. `turn.ts`
   * runs it, for the same reason the Race Start Event does not run its own
   * PROBLEM: `duel.ts` owns duels and a second copy of one is how two versions
   * of a rule start to drift.
   */
  duelInstigatorId: string | null;
  duelTargetId: string | null;
  /** The BGC Challenge Quality, or null when the card named none (D-EZ-32). */
  quality: Quality | 'TV' | null;
  /** Exactly what the track card printed, for the ledger. */
  printedCell: string | null;
  challenges: ChurnChallenge[];
  /** Cars awarded a BGC performance chip on the Porch (D-EZ-39). One entry per chip. */
  bgcChipsAwarded: string[];
  /**
   * BG chips actually spent inside this Churn, and by whom. Not `ChipDelta`s —
   * see `BgChipLoss` in `ruleEvent.ts` for why a token of a different colour is
   * kept off the performance-chip ledger.
   */
  bgcChipsSpent: { driverId: string; chips: number; reason: string }[];
  /**
   * Awards §3.1's cap of two refused (D-EZ-39). Counted rather than dropped: the
   * cap is a ruling, and a ruling whose cost is invisible is one nobody revisits.
   */
  bgcChipsRefused: { driverId: string; reason: BgChipRefusal }[];
  /**
   * Cars that held a BG chip when this Churn was played as a DUEL and so could
   * not offer it — the exact size of the gap D-EZ-39(b) records.
   */
  bgcChipsIdleInDuel: string[];
  /** Triples and triangles, for `turn.ts` to resolve as single-car events. */
  pendingProblems: PendingProblem[];
  /**
   * The running order the Churn left behind. **Authoritative** — the caller
   * installs this array rather than replaying `positionDeltas`.
   *
   * It carries the CARDS, not just the ids, and that is load-bearing: a laps-
   * down car who churned his way back onto the lead lap has had `lapsDown`
   * cleared and his parked trait chips handed back on the object in here. A
   * caller that rebuilt the order from ids against its own copy of the field
   * would silently drop both, and the end-of-turn boundary clamp would then
   * push the escaped car straight back below the marker — a rule that fires,
   * logs, and does nothing.
   */
  order: Car[];
  /** The same order as ids, for the ledger and for tests. */
  finalOrder: string[];
  /**
   * Did this Churn spend its one-third increment? False for an abort (D-EZ-31).
   * The caller feeds this straight back to `ChurnTracker.note`.
   */
  occurred: boolean;
  /** Why it aborted, when it did. Reported rather than silently skipped. */
  abortReason: string | null;
}

export interface ChurnCamRoll {
  black: number;
  metallic: [number, number];
  total: number;
  doubles: boolean;
}

/**
 * Read the metallic total on the track card.
 *
 * Supplied by the caller rather than done here because the track card lives in
 * `turn.ts`, which imports this module — a runtime import cycle is not worth
 * the tidiness, and this keeps the Churn's own dice in the Churn.
 */
export type ChurnCamReader = (roll: ChurnCamRoll) => { result: RaceCamResult; printed: string };

export interface ChurnInput {
  /**
   * The running order, index 0 = leader. Cars still running only.
   *
   * `Car.position` is NOT read — array index is the running order, because the
   * stamped numbers are stale mid-turn and `renumber` does not run until the
   * turn concludes. Every position this module uses is derived from the array.
   */
  order: readonly Car[];
  dice: Dice;
  /** Reads the metallic total on this race's track card, footnotes and all. */
  readCam: ChurnCamReader;
  /** The LDSG's memory, or null when the laps-down optional rule is off. */
  lapsDown?: LapsDownTracker | null;
  /**
   * The prelude d10 that fired this Churn, so the ledger shows the roll that
   * caused the event alongside the rolls the event made. Passed in rather than
   * rolled here because a caller has to be able to ask "does a Churn happen?"
   * without paying for the rest — see `rollChurnTrigger`.
   */
  triggerRecord?: DiceRecord;
  /**
   * RB/xiv's solitaire auto-spend, for the BG chips a Churn challenge may put in
   * play (D-EZ-39). Default true, exactly as `challenge.ts` and `duel.ts` do it,
   * so head-to-head play can switch all three off together.
   */
  useChips?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Mirrors `turn.ts`'s `isQualityCall`; re-stated here to avoid a module cycle. */
function isQuality(result: RaceCamResult): result is Quality | 'TV' {
  return result === 'TV' || (QUALITIES as readonly string[]).includes(result);
}

/**
 * A `TroubleField` view of a working order, positions taken from the array
 * rather than from `Car.position` — the cards have been moving and the stamped
 * numbers are stale until `renumber` runs at the end of the turn.
 */
function fieldOf(order: readonly Car[]): TroubleField {
  return troubleFieldFromGrid(order.map((c, i) => (c.position === i + 1 ? c : { ...c, position: i + 1 })));
}

/** 1-based position in a working order, or 0 when the car is gone. */
function positionIn(order: readonly Car[], driverId: string): number {
  return order.findIndex((c) => c.card.driverId === driverId) + 1;
}

function bottomGroupOf(order: readonly Car[]): Car[] {
  return order.filter((_, i) => groupOf(i + 1) === 'bottom');
}

// ---------------------------------------------------------------------------
// The Churn
// ---------------------------------------------------------------------------

/**
 * Resolve a Bottom Group Churn that has already been triggered and allowed.
 *
 * This module owns the PROCEDURE — the cam read, who challenges whom, in what
 * order, and what an exchange does to the stack. It deliberately does NOT run
 * the duel or the problems it raises: `duel.ts` and `problem.ts` own those,
 * they need the whole turn context, and duplicating either here is how a rule
 * ends up with two implementations that slowly disagree. The result says what
 * is needed; the turn runs it. That is the same division the Race Start Event
 * uses, and for the same reason.
 */
export function resolveBottomGroupChurn(input: ChurnInput): BottomGroupChurnResult {
  const { dice } = input;
  const useChips = input.useChips ?? true;
  const startOrder = [...input.order];
  const work = [...startOrder];
  const diceRecords: DiceRecord[] = input.triggerRecord ? [input.triggerRecord] : [];
  const modifiers: Modifier[] = [];
  const challenges: ChurnChallenge[] = [];
  const pendingProblems: PendingProblem[] = [];
  const bgcChipsAwarded: string[] = [];
  const bgcChipsSpent: BottomGroupChurnResult['bgcChipsSpent'] = [];
  const bgcChipsRefused: BottomGroupChurnResult['bgcChipsRefused'] = [];
  const bgcChipsIdleInDuel: string[] = [];

  /**
   * Write a car back into the working order by ID rather than by index.
   *
   * Chip changes never move anybody, but a challenge that succeeds does — so
   * resolving chips by id and moving by position keeps the two from tripping
   * over each other regardless of which happens first.
   */
  const update = (driverId: string, next: Car): void => {
    const i = work.findIndex((c) => c.card.driverId === driverId);
    if (i !== -1) work[i] = next;
  };

  const finish = (
    momentTag: ChurnMoment,
    description: string,
    extra: {
      asDuel?: boolean;
      duelInstigatorId?: string | null;
      duelTargetId?: string | null;
      quality?: Quality | 'TV' | null;
      printedCell?: string | null;
      black?: number;
      metallic?: [number, number];
      metallicTotal?: number;
      occurred: boolean;
      abortReason?: string | null;
    },
  ): BottomGroupChurnResult => ({
    eventType: 'bottom-group-churn',
    momentTag,
    ruleCitation: `${CITE_BGC}; ${CITE_BGC_TRIGGER}`,
    actorIds: [
      ...new Set(
        [
          extra.duelInstigatorId ?? null,
          extra.duelTargetId ?? null,
          ...challenges.flatMap((c) => [c.challengerId, c.defenderId]),
        ].filter((id): id is string => id !== null),
      ),
    ],
    positionDeltas: diffOrder(startOrder, work),
    modifiers,
    dice: diceRecords,
    description,
    black: extra.black ?? 0,
    metallic: extra.metallic ?? [0, 0],
    metallicTotal: extra.metallicTotal ?? 0,
    asDuel: extra.asDuel ?? false,
    duelInstigatorId: extra.duelInstigatorId ?? null,
    duelTargetId: extra.duelTargetId ?? null,
    quality: extra.quality ?? null,
    printedCell: extra.printedCell ?? null,
    challenges,
    bgcChipsAwarded,
    bgcChipsSpent,
    bgcChipsRefused,
    bgcChipsIdleInDuel,
    pendingProblems,
    order: [...work],
    finalOrder: work.map((c) => c.card.driverId),
    occurred: extra.occurred,
    abortReason: extra.abortReason ?? null,
  });

  // ── ABORT 1: nothing to churn ──────────────────────────────────────────────
  //
  // "All cars in Bottom Group (BG), including the Lap Down Sub-Group (LDSG) are
  // eligible." With fewer than two of them there is no pair to duel and nobody
  // to exchange positions with, so the procedure cannot be carried out at all.
  // Checked BEFORE the race cam is rolled: a Churn that cannot happen must not
  // spend dice, or it would repaint every seeded race behind it.
  const bgAtStart = bottomGroupOf(work);
  if (bgAtStart.length < 2) {
    modifiers.push({
      name: 'BOTTOM group cars',
      value: bgAtStart.length,
      detail: CITE_BGC_ELIGIBLE,
    });
    return finish('churn.aborted', 'The Churn is called off — there is barely a bottom group left to churn.', {
      occurred: false,
      abortReason: `only ${bgAtStart.length} car${bgAtStart.length === 1 ? '' : 's'} in the BOTTOM group`,
    });
  }

  // ── FIRST STEP — "Roll Race Cam Dice" ─────────────────────────────────────
  const roll = dice.raceCam();
  diceRecords.push({
    kind: 'd6',
    dice: [roll.black],
    label: 'BGC race cam black die',
    faces: { black: roll.black },
  });
  diceRecords.push({
    kind: '2d6-sum',
    dice: roll.metallic,
    total: roll.total,
    label: 'BGC race cam metallic total',
    flags: { doubles: roll.doubles },
  });

  // "Multiple Events (RB/iii) are ignored during a Churn." Recorded rather than
  // dropped in silence: doubles came up, and the ledger should show that the
  // rule they would normally trigger was considered and set aside.
  if (roll.doubles) {
    modifiers.push({
      name: 'MULTIPLE EVENTS',
      value: 'ignored',
      detail: CITE_BGC_MULTIPLE_EVENTS,
    });
  }

  const camFacts = { black: roll.black, metallic: roll.metallic, metallicTotal: roll.total };

  // ── THE DUEL BRANCH ───────────────────────────────────────────────────────
  //
  // "If the black die is a 6 play the BGC as duel. The Bottom Group Driver
  // Finder Table will be used to select the dueling cars ... The duel ends the
  // BGC." Two finder rolls, because the table names ONE car and the sentence
  // asks for two ("the dueling cars").
  if (roll.black === BGC_DUEL_BLACK_FACE) {
    const first = rollDriverFinder('bottom', fieldOf(work), dice);
    diceRecords.push(first.dice);
    modifiers.push(...first.modifiers);

    let second = rollDriverFinder('bottom', fieldOf(work), dice);
    diceRecords.push(second.dice);
    modifiers.push(...second.modifiers);

    // AMBIGUITY: nothing printed says what to do when the table names the same
    // car twice. Re-rolled, up to a bounded number of attempts, because a
    // driver cannot duel himself (`duel.ts` throws on it) and because the
    // alternative — voiding the whole Churn on a 1-in-24 collision — would
    // quietly delete a twelfth of every duel Churn. Bounded rather than looped
    // so a two-car bottom group cannot hang the engine.
    for (let attempt = 0; attempt < 8 && second.car && first.car && second.car.driverId === first.car.driverId; attempt++) {
      second = rollDriverFinder('bottom', fieldOf(work), dice);
      diceRecords.push(second.dice);
      modifiers.push({
        name: 'duel finder re-rolled',
        value: 'the table named the same car twice',
        detail: 'engine ruling, not printed — a driver cannot duel himself (RB/ix)',
      });
    }

    const a = first.car;
    const b = second.car && a && second.car.driverId !== a.driverId ? second.car : null;
    if (!a || !b) {
      return finish('churn.aborted', 'The Churn calls for a duel and the finder cannot produce two cars for it.', {
        ...camFacts,
        asDuel: true,
        occurred: false,
        abortReason: 'the BOTTOM GROUP DRIVER FINDER could not name two different cars',
      });
    }

    modifiers.push({ name: 'BGC played as', value: 'DUEL', detail: CITE_BGC_DUEL });

    // D-EZ-39(b) — the gap, counted. `duel.ts` owns duels and reads `Car.chips`;
    // a BG chip is a separate pile and is not lent across that boundary. If
    // either duellist is holding one it sits idle, and the harness prints how
    // often that happens so the size of the omission is a number on screen
    // rather than a sentence in a comment.
    for (const c of [a, b]) {
      const live = work.find((w) => w.card.driverId === c.driverId);
      if (live && bgChipsOf(live) > 0) bgcChipsIdleInDuel.push(c.driverId);
    }
    if (bgcChipsIdleInDuel.length > 0) {
      modifiers.push({
        name: 'BG chips idle in the duel',
        value: bgcChipsIdleInDuel.length,
        detail: CITE_BGC_DUEL_NO_CHIPS,
      });
    }

    return finish(
      'churn.duel',
      `A Bottom Group Churn, and the black die says it settles itself: ${a.name ?? a.driverId} and ` +
        `${b.name ?? b.driverId} go at it, and that is the whole Churn.`,
      {
        ...camFacts,
        asDuel: true,
        duelInstigatorId: a.driverId,
        duelTargetId: b.driverId,
        occurred: true,
      },
    );
  }

  // "Ignore the black die for all other BGC purposes."
  modifiers.push({
    name: 'black die',
    value: roll.black,
    detail: 'EZRA-S2 §3.12 — "Ignore the black die for all other BGC purposes."',
  });

  // ── THE QUALITY ───────────────────────────────────────────────────────────
  //
  // "If no duel occurs, the MM dice are used on the Track Card to select a BGC
  // Challenge Quality."
  const read = input.readCam(roll);
  modifiers.push({ name: 'printed cell', value: read.printed, detail: CITE_BGC_QUALITY });

  // ── ABORT 2: the card named no quality (D-EZ-32) ──────────────────────────
  if (!isQuality(read.result)) {
    return finish(
      'churn.aborted',
      `The Churn reads ${read.result} off the track card — that is not a quality, so there is nothing ` +
        `for the bottom group to race for and the Churn is set aside.`,
      {
        ...camFacts,
        printedCell: read.printed,
        occurred: false,
        abortReason: `the track card cell for a metallic ${roll.total} reads ${read.result}, which designates no quality (D-EZ-32)`,
      },
    );
  }
  const quality: Quality | 'TV' = read.result;
  modifiers.push({ name: 'BGC Challenge Quality', value: quality, detail: CITE_BGC_QUALITY });

  // ── WHO PARTICIPATES ──────────────────────────────────────────────────────
  //
  // "Cars assigned the designated quality will participate in the BGC."
  // "Starting at the back of the BG" — trailing car first, which is also the
  // base game's own challenge order (RB/vi), and which puts the LDSG first
  // because the LDSG is by construction the tail of the field.
  //
  // Snapshotted before any challenge is resolved, for the same reason
  // `challenge.ts` snapshots its list: the entitled set is fixed when the cam is
  // read, and a car knocked backwards mid-Churn does not thereby earn a turn.
  //
  // "E can never challenge out of the LDSG" (§3.11) — note the wording: he does
  // not CHALLENGE, rather than challenging and being denied. A roll whose only
  // possible outcome is nothing is not a roll the sheet asks for, and rolling it
  // anyway would consume dice and desynchronise every seeded race downstream.
  const challengers = bottomGroupOf(work)
    .filter((c) => hasQuality(c, quality))
    .filter((c) => !(c.lapsDown === true && !canChallengeOutOfLdsg(c.card.pitCrewGrade)))
    .sort((a, b) => positionIn(work, b.card.driverId) - positionIn(work, a.card.driverId));

  // ── ABORT 3: nobody holds it ──────────────────────────────────────────────
  if (challengers.length === 0) {
    return finish(
      'churn.aborted',
      `The Churn calls ${quality} and not one car in the bottom group has it. Nothing happens.`,
      {
        ...camFacts,
        quality,
        printedCell: read.printed,
        occurred: false,
        abortReason: `no eligible BOTTOM group car holds ${quality}`,
      },
    );
  }

  /**
   * Who may take the Porch challenge.
   *
   * Two printed cases, and this is exactly their union and no more:
   *
   *   (a) *"The car that occupies the first (lead) position in the BG (BG#20)
   *        is designated the car on the 'Porch'. If it is found to possess the
   *        active BGC quality, it may challenge a MG car in the MG 19th
   *        position."* — the car sitting on the Porch when the Churn begins.
   *
   *   (b) *"If a *quality moves a car to the 'porch' on the first challenge, he
   *        can then challenge the MG car in the 19th position."* — a STARRED
   *        challenger who won his way onto it, taking the Porch challenge as
   *        the second challenge his star grants him (RB/vii).
   *
   * Case (a) is also why the Porch rule exists at all: the car at BG#20 has
   * nobody ahead of him inside the bottom group, so "challenge ahead using the
   * BG Driver Finder Table" can only ever name a car behind him, and the finder
   * challenge is ignored by its own rule. The Porch challenge IS his challenge.
   *
   * An unstarred car that wins its way onto the Porch is deliberately NOT added:
   * case (b) says "*quality", and a rule that hands out performance chips is not
   * one to widen by inference.
   */
  const porchEligible = new Set<string>();
  const porchCarAtStart = work[PORCH_POSITION - 1];
  if (porchCarAtStart) porchEligible.add(porchCarAtStart.card.driverId);

  const ldsgEscapes: string[] = [];

  /** One challenge, either against the finder's pick or against MG 19th. */
  const runOne = (challenger: Car, attempt: 1 | 2): ChurnChallenge | null => {
    const challengerId = challenger.card.driverId;
    const from = positionIn(work, challengerId);
    if (from === 0) return null; // gone from the board earlier in this Churn

    const onPorch = from === PORCH_POSITION && porchEligible.has(challengerId);

    // ---- who is being challenged -----------------------------------------
    let defenderPosition: number | null = null;
    let ignoredReason: string | null = null;

    if (onPorch) {
      // "it may challenge a MG car in the MG 19th position" — named outright,
      // so no finder roll.
      defenderPosition = PORCH_TARGET_POSITION;
      if (!work[PORCH_TARGET_POSITION - 1]) {
        ignoredReason = 'the MIDDLE group has nobody in 19th to challenge';
      }
    } else {
      const finder = rollDriverFinder('bottom', fieldOf(work), dice);
      diceRecords.push(finder.dice);
      modifiers.push(...finder.modifiers);
      const named = finder.car;
      if (!named) {
        ignoredReason = 'the BOTTOM GROUP DRIVER FINDER named nobody';
      } else {
        const namedPos = positionIn(work, named.driverId);
        if (namedPos === 0) {
          ignoredReason = 'the car the finder named is no longer on the board';
        } else if (namedPos >= from) {
          // "If the finder designates a car behind the challenger, the
          // challenge is ignored." A finder that names the challenger himself
          // is the same case — there is nobody ahead to pass.
          ignoredReason =
            namedPos === from
              ? 'the finder named the challenger himself'
              : `the finder named P${namedPos}, which is behind P${from}`;
        } else {
          defenderPosition = namedPos;
        }
      }
    }

    if (defenderPosition === null || ignoredReason !== null) {
      return {
        challengerId,
        challengerName: nameOf(challenger),
        attempt,
        from,
        defenderId: null,
        defenderName: null,
        defenderPosition: null,
        porch: onPorch,
        ignored: true,
        ignoredReason,
        voided: false,
        gasOk: false,
        defended: false,
        brake: null,
        rawSuccess: false,
        success: false,
        bgChipsHeld: { challenger: 0, defender: 0 },
        chip: 'none',
        oddEvenDie: null,
        awardedBgChip: false,
        bgChipRefused: null,
        ldsg: null,
      };
    }

    const defender = work[defenderPosition - 1];

    // ---- the roll (D-EZ-33) ----------------------------------------------
    const rwb = dice.challenge();
    diceRecords.push({
      kind: 'd6-set',
      dice: [rwb.red, rwb.white, rwb.blue],
      label: CHALLENGE_ROLL,
      faces: { red: rwb.red, white: rwb.white, blue: rwb.blue },
      flags: { triple: rwb.triple },
    });

    // RB/vi — "If the dice roll is 'triples' ... disregard the challenge and
    // instead roll for a problem for the challenging driver on the PROBLEM
    // FINDER chart." RB/xvi — a red 6 on a triangle car is an immediate
    // unspecified PROBLEM. Both void the challenge and hand the challenger a
    // problem, which §3.12 then scopes: "resolved as single car events in the
    // same fashion as for duels (RB/ix). The Advanced Trouble Matrix rule is
    // not applied."
    if (rwb.triple || (challenger.card.triangle && rwb.red === 6)) {
      const source = rwb.triple ? 'triples' : 'triangle';
      pendingProblems.push({
        driverId: challengerId,
        source,
        specified: rwb.triple,
        ruleCitation: `${rwb.triple ? 'RB/vi' : 'RB/xvi'}; ${CITE_BGC_INCIDENTS}`,
        description: rwb.triple
          ? `${nameOf(challenger)} rolls triples in the Churn and has a problem instead of a challenge.`
          : `${nameOf(challenger)} shows a red 6 on a triangle car in the Churn — an immediate problem.`,
      });
      modifiers.push({
        name: rwb.triple ? 'triples' : 'triangle-symbol',
        value: 0,
        detail: CITE_BGC_INCIDENTS,
      });
      return {
        challengerId,
        challengerName: nameOf(challenger),
        attempt,
        from,
        defenderId: defender.card.driverId,
        defenderName: nameOf(defender),
        defenderPosition,
        porch: onPorch,
        ignored: false,
        ignoredReason: null,
        voided: true,
        gasOk: rwb.red <= challenger.card.gasPedal,
        defended: false,
        brake: null,
        rawSuccess: false,
        success: false,
        bgChipsHeld: { challenger: 0, defender: 0 },
        chip: 'none',
        oddEvenDie: null,
        awardedBgChip: false,
        bgChipRefused: null,
        ldsg: null,
      };
    }

    const gasOk = rwb.red <= challenger.card.gasPedal;
    const brake = brakePedal(defender, defenderPosition, quality);
    const defended = rwb.blue <= brake.effective;
    // RB/vii — "The challenge is successful if TWO things happen: (1) the
    // challenging driver rolls within his 'gas pedal' range, AND (2) the
    // challenged driver rolls OUTSIDE his 'brake pedal' range."
    const rawSuccess = gasOk && !defended;
    modifiers.push(...brake.bonuses);

    // ---- the BG performance chip contest (D-EZ-39) ------------------------
    //
    // "Performance Chips will be allowed, but these have no affect outside the
    // BG. They function only within the BG and then only during a BGC."
    //
    // `bgChipsSpendable` is the gate for both halves of that sentence, and it is
    // asked for each side at the position each side actually occupies. The
    // contest itself is `challenge.ts`'s, unchanged, so EZRA §3.1's dominance
    // rule and RB/xiv's odd/even die are the ones the rest of the game uses.
    //
    // THE DEFENDER OF A PORCH CHALLENGE IS EXCLUDED (D-EZ-40). He is a MIDDLE
    // group car, his chips are ordinary chips, and — decisively — he loses
    // nothing if the challenge succeeds: "it does not advance to the MG, but
    // receives a BGC performance chip". There is no position to defend, so
    // neither RB/xiv's "salvage a failed... defense" nor §3.1's list of chip
    // uses reaches him. `defenderMayDefend: false` says exactly that.
    const live = work[from - 1] ?? challenger;
    const challengerBgChips = bgChipsSpendable(live, { position: from, phase: 'bottom-group-churn' });
    const defenderBgChips = onPorch
      ? 0
      : bgChipsSpendable(defender, { position: defenderPosition, phase: 'bottom-group-churn' });
    const contest = resolveChipContest({
      rawSuccess,
      challengerId,
      challengerChips: onPorch && !PORCH_CHALLENGE_SPENDS_CHIPS ? 0 : challengerBgChips,
      defenderId: defender.card.driverId,
      defenderChips: defenderBgChips,
      dice,
      enabled: useChips,
      defenderMayDefend: !onPorch,
      venue: 'challenge',
    });
    diceRecords.push(...oddEvenRoll(contest.oddEvenDie));
    modifiers.push(...contest.modifiers);
    if (contest.modifiers.length > 0) {
      modifiers.push({ name: 'BG chips in play', value: `${challengerBgChips} v ${defenderBgChips}`, detail: CITE_BGC_CHIPS_ALLOWED });
    }
    for (const delta of contest.chipDeltas) {
      if (delta.delta >= 0) continue;
      const holder = work.find((c) => c.card.driverId === delta.driverId);
      if (!holder) continue;
      const spend = spendBgChips(holder, -delta.delta);
      if (spend.spent === 0) continue;
      update(delta.driverId, spend.car);
      bgcChipsSpent.push({ driverId: delta.driverId, chips: spend.spent, reason: delta.reason });
    }
    const success = contest.success;

    let awardedBgChip = false;
    let bgChipRefused: BgChipRefusal | null = null;
    let ldsg: ChurnChallenge['ldsg'] = null;

    if (success && onPorch) {
      // "If successful, it does not advance to the MG, but receives a BGC
      // performance chip." / "If successful is awarded a BG PC and holds the
      // 20th position." The card does NOT move — which is also the only way
      // "During a BGC cars will not advance to Middle Group" and a challenge
      // aimed at 19th place can both be true.
      //
      // The award goes through `awardBgChip`, which refuses outside the BOTTOM
      // group and at §3.1's cap of two — see D-EZ-39. `from` is the Porch, so
      // only the cap can bite here, and when it does it is recorded rather than
      // dropped.
      const holder = work[from - 1];
      const given = holder ? awardBgChip(holder, from) : { car: holder, awarded: 0, refused: 'outside-the-bottom-group' as const };
      if (given.awarded > 0 && given.car) {
        update(challengerId, given.car);
        awardedBgChip = true;
        for (let n = 0; n < given.awarded; n++) bgcChipsAwarded.push(challengerId);
        modifiers.push({ name: 'BGC performance chip', value: +given.awarded, detail: CITE_BGC_PORCH });
      }
      if (given.refused) {
        bgChipRefused = given.refused;
        bgcChipsRefused.push({ driverId: challengerId, reason: given.refused });
        modifiers.push({ name: 'BGC chip refused', value: given.refused, detail: CITE_BG_CHIP_CAP });
      }
      modifiers.push({ name: 'holds the 20th position', value: 'no advance to MIDDLE', detail: CITE_BGC_NO_PROMOTION });
    } else if (success && challenger.lapsDown === true && defender.lapsDown !== true) {
      // "If the successful challenger is a car in the LDSG, EZRA challenge and
      // movement rules for that group are observed." A lapped car that beats a
      // car ON THE LEAD LAP is not passing him on the road — he is a lap behind
      // — so this is §3.11's escape and not §3.12's exchange: "the driver moves
      // to the last position in the LLSG, subject to Pit crew rating". The
      // defender keeps his place.
      const grade = challenger.card.pitCrewGrade;
      const needed = ESCAPE_CHALLENGES[grade] ?? 0;
      const tracker = input.lapsDown ?? null;
      const escaped = tracker ? tracker.bankWin(challengerId, grade) : needed <= 1;
      const banked = tracker ? (escaped ? needed : tracker.winsBanked(challengerId)) : needed;
      if (escaped) {
        returnToLeadLap(work, challengerId, 'churned his way back onto the lead lap');
        tracker?.cameBack(challengerId);
        ldsgEscapes.push(challengerId);
      }
      ldsg = { banked, needed, escaped };
      modifiers.push({
        name: escaped ? 'LDSG escape' : 'LDSG win banked',
        value: `${banked}/${needed}`,
        detail: CITE_BGC_LDSG,
      });
    } else if (success) {
      // "If the challenger successfully challenges a car ahead the two exchange
      // positions." A straight swap — both cars are in the same sub-group, so
      // the laps-down boundary is not crossed and the end-of-turn clamp has
      // nothing to undo.
      swapPositions(work, from, defenderPosition);
    }
    // "No penalties accrue for failure." A failed Churn challenge does nothing
    // at all — no relegation, no chip, no trouble.

    return {
      challengerId,
      challengerName: nameOf(challenger),
      attempt,
      from,
      defenderId: defender.card.driverId,
      defenderName: nameOf(defender),
      defenderPosition,
      porch: onPorch,
      ignored: false,
      ignoredReason: null,
      voided: false,
      gasOk,
      defended,
      brake,
      rawSuccess,
      success,
      bgChipsHeld: { challenger: challengerBgChips, defender: defenderBgChips },
      chip: contest.outcome,
      oddEvenDie: contest.oddEvenDie,
      awardedBgChip,
      bgChipRefused,
      ldsg,
    };
  };

  for (const challenger of challengers) {
    const first = runOne(challenger, 1);
    if (!first) continue;
    challenges.push(first);

    // A car that has just had a PROBLEM is being set aside for a repair roll; it
    // does not go on to take a starred second challenge. Same reading
    // `challenge.ts` takes of the same rule.
    if (first.voided) continue;

    // RB/vii — a star grants a second challenge. "Qualities are played during
    // the Churn in the normal fashion, but only within the BG", so the second
    // one stays inside the bottom group; the single printed exception is the
    // Porch, and case (b) above is exactly it.
    if (!isStarred(challenger, quality)) continue;

    const now = positionIn(work, challenger.card.driverId);
    if (now === 0) continue;
    if (first.success && now === PORCH_POSITION) porchEligible.add(challenger.card.driverId);
    // A lapped car that escaped to the lead lap has left the group this Churn
    // is about; his second challenge would be a MIDDLE group challenge, and
    // "During a BGC cars will not advance to Middle Group."
    if (ldsgEscapes.includes(challenger.card.driverId)) continue;

    const second = runOne(work[now - 1], 2);
    if (second) challenges.push(second);
  }

  const made = challenges.filter((c) => !c.ignored).length;
  const stuck = challenges.filter((c) => c.success).length;
  const porchTaken = challenges.some((c) => c.porch && !c.ignored);

  // ── ABORT 4: everything was ignored ───────────────────────────────────────
  //
  // Every entitled car's challenge was thrown out by the finder's own rule, so
  // no challenge was made and the Churn did nothing. Same reasoning as the
  // other three aborts (D-EZ-31): started, not completed.
  if (made === 0) {
    return finish(
      'churn.aborted',
      `The Churn calls ${quality}, and every car that has it is looking at somebody behind them. Nothing happens.`,
      {
        ...camFacts,
        quality,
        printedCell: read.printed,
        occurred: false,
        abortReason: 'every challenge was ignored — the finder never named a car ahead',
      },
    );
  }

  return finish(
    porchTaken ? 'churn.porch' : 'churn.challenges',
    `A Bottom Group Churn on ${quality}: ${challengers.length} car${challengers.length === 1 ? '' : 's'} had it, ` +
      `${made} challenge${made === 1 ? '' : 's'} went in, ${stuck} stuck` +
      (bgcChipsAwarded.length > 0 ? `, and the car on the porch got a chip out of it` : '') +
      (ldsgEscapes.length > 0
        ? `. ${ldsgEscapes.length} car${ldsgEscapes.length === 1 ? '' : 's'} churned back onto the lead lap`
        : '') +
      '.',
    {
      ...camFacts,
      quality,
      printedCell: read.printed,
      occurred: true,
    },
  );
}

// ---------------------------------------------------------------------------
// THE BOTTOM GROUP SURGE — BUILT, IN ITS OWN FILE
// ---------------------------------------------------------------------------

/**
 * EZRA-S2 §3.13 BOTTOM GROUP SURGE is implemented in `bottomGroupSurge.ts`.
 *
 * This note used to say it was not built, because §3.13's trigger face is not
 * printed anywhere in the corpus and choosing one would have been choosing the
 * rate. That reasoning has not changed and the face is still not printed — see
 * D-EZ-35 in `bottomGroupSurge.ts`, which reproduces the sentence, records where
 * it was looked for, and lists the candidate readings. What changed is the
 * handling: the printed PROCEDURE is built in full, and the unprinted TRIGGER is
 * an explicit, named, citable open ruling that ships **off**, so nothing fires
 * until Howard rules and no rate has been invented.
 *
 * The ordering between the two preludes was already settled by the Churn's own
 * text — *"all other aspects of that turn ... including a Bottom Group Surge,
 * are resolved after the BGC has completed"* — and `turn.ts` runs them in that
 * order. In practice they can only meet on a FINAL turn: a Churn cannot occur on
 * a Pit Turn and a Surge occurs only on *"any Pit or Final race turn"*.
 */
export const SURGE_IS_BUILT_ELSEWHERE = CITE_BGS_SEE_SURGE_MODULE;
